// PLP toolbar (related-collections chips + quick size-filter bar).
//
// The size pills live OUTSIDE the main-collection section, but toggling one
// must re-render the product grid. Section ids aren't knowable across sections
// in Liquid, so on connect we resolve the grid's section id from
// #results-list[section-id] and retarget the toolbar's facets-form-component(s)
// to it. The bar's own active states are refreshed on collection/search update
// events: we ask for this section's HTML at once, wait for the grid render to
// finish (event.promise), and only then morph the facets-form(s) — the
// related-collections chips don't depend on filters, and morphing the whole
// section mid view transition causes visible flashes.

import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { startViewTransition } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

// What this section's stylesheet asks about when it decides whether a bar's wrapper takes a row
// of its own or dissolves through `display: contents` - the only thing that changes the toolbar's
// height, and so the only thing that makes the grid below it move. Kept in step by hand with the
// `:has()` rules there: add a variant to one and this has to learn about it too.
const TOOLBAR_LAYOUT_CLASSES = [
  'size-filter-bar--active-filters',
  'size-filter-bar--floating',
  'size-filter-bar--floating-collapsed',
  'size-filter-bar--toggle-only',
];

/**
 * The toolbar's shape as a string, for comparing what is on screen against what is about to
 * replace it. Which variant each bar is in, and whether it holds any pills, is the whole of what
 * those rules read.
 * @param {ParentNode} root
 * @returns {string}
 */
function toolbarShape(root) {
  return [...root.querySelectorAll('.size-filter-bar')]
    .map((bar) => {
      const variants = TOOLBAR_LAYOUT_CLASSES.filter((name) => bar.classList.contains(name));

      return `${variants.join('.')}:${bar.querySelector('facet-remove-component') ? 'pills' : 'empty'}`;
    })
    .join('|');
}

class PlpToolbar extends HTMLElement {
  connectedCallback() {
    this.#retargetFacetsForms();
    this.#lockPendingBars();
    document.addEventListener(StandardEvents.collectionUpdate, this.#refresh);
    document.addEventListener(StandardEvents.searchUpdate, this.#refresh);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.collectionUpdate, this.#refresh);
    document.removeEventListener(StandardEvents.searchUpdate, this.#refresh);
  }

  #retargetFacetsForms() {
    const targetSectionId = document.getElementById('results-list')?.getAttribute('section-id');
    if (!targetSectionId) return;

    for (const form of this.querySelectorAll('facets-form-component')) {
      form.setAttribute('section-id', targetSectionId);
    }
  }

  // A pending bar is only painted as skeleton chips, and paint leaves them in the tab
  // order: without this a keyboard user reaches an invisible pill and applies a count we
  // have just declared unreliable. Here and not in Liquid, because the skeleton itself is
  // gated on `(scripting: enabled)` - with no JS the availability filter never lands, so a
  // server-rendered `disabled` would leave those shoppers a dead bar. The morph in #refresh
  // rewrites the attribute from the fetched HTML, so the chips that are genuinely out of
  // stock keep it and every other one loses it.
  #lockPendingBars() {
    for (const bar of this.querySelectorAll('.size-filter-bar--pending')) {
      bar.setAttribute('aria-busy', 'true');

      for (const chip of bar.querySelectorAll('.size-filter-bar__chip')) {
        chip.disabled = true;
      }
    }
  }

  /**
   * Index-based pairing below relies on an invariant: every size-filter-bar
   * variant renders exactly ONE facets-form-component (the count fallbacks
   * wrap their toggles in one too) and no other toolbar block renders any,
   * so live DOM and fetched HTML always pair 1:1. Even when a filter change
   * flips the bar to a different variant, morphing across variants is the
   * desired outcome. Revisit the pairing if another block ever adds forms.
   * @param {{ promise?: Promise<unknown> }} [event]
   */
  #refresh = (event) => {
    const section = this.closest('.shopify-section');
    const forms = [...this.querySelectorAll('facets-form-component')];
    if (!section || !forms.length) return;

    this.toggleAttribute('data-updating', true);

    const gridRendered = event?.promise instanceof Promise ? event.promise.catch(() => {}) : Promise.resolve();

    // Asked for now rather than after the grid has finished. This HTML depends on the filters, and
    // facets.js writes those into the URL before it renders anything, so the answer is already the
    // right one - `getSectionHTML` reads the location when it is called. Waiting put a whole round
    // trip on the critical path, and it waited for the grid's transition to *finish*, so a network
    // request sat behind an animation and the bar landed seconds after the products it describes.
    // Only the asking moved: the morph below still waits for the grid.
    const toolbarHtml = sectionRenderer.getSectionHTML(normalizeSectionId(section.id), false);

    // The chain below is what handles a failed fetch, but it does not attach until the grid
    // settles, and an unhandled rejection in between would be reported as one. This branch exists
    // to be that handler in the meantime and does nothing else.
    toolbarHtml.catch(() => {});

    gridRendered
      .then(() => toolbarHtml)
      .then((html) => {
        const responseDocument = new DOMParser().parseFromString(html, 'text/html');
        const newForms = responseDocument.querySelectorAll('facets-form-component');

        const applyRefresh = () => {
          forms.forEach((form, index) => {
            if (newForms[index]) morph(form, newForms[index], MORPH_OPTIONS);
          });

          this.#retargetFacetsForms();

          // The filtered counts have landed, so the bar can show and answer again. Cleared
          // here and not by the morph: MORPH_OPTIONS is `childrenOnly`, so the root
          // element's attributes - its class list and aria-busy with them - are never
          // rewritten. The chips' own `disabled` is, which is why it is not touched here.
          for (const bar of this.querySelectorAll('.size-filter-bar--pending')) {
            bar.classList.remove('size-filter-bar--pending');
            bar.removeAttribute('aria-busy');
          }
        };

        // Not while a drawer is open. The shopper is looking at the panel, the toolbar is
        // behind its backdrop, and a drawer cannot be lifted over the travelling grid the
        // way the other floating elements are: the panel could take a
        // `view-transition-name`, its `::backdrop` cannot, so the grid would still slide
        // over the dimming. facets.js skips its own transition inside a dialog for the
        // same kind of reason.
        if (document.querySelector('theme-drawer[open]')) return applyRefresh();

        // Inside a view transition of its own, so the rows that come and go with the
        // filters fade instead of blinking. The browser compares two snapshots, which
        // costs no per-frame layout and works on an element that leaves via
        // `display: contents` - CSS transitions can do neither, since that display
        // value generates no box for opacity to act on.
        //
        // Its own and not the grid's: the grid's is long finished by here, because
        // `startViewTransition` resolves on `finished` and this chain waits for it.
        // Only the facets forms are morphed, never the whole section - doing that
        // mid-transition is what caused the flashes this file warns about above.
        // The second type only when this refresh changes the toolbar's shape, which is the only
        // time anything below it moves. It is what the stylesheet names the grid on, and naming
        // the grid means snapshotting the whole of it: taken twice in one gesture - once for the
        // grid's own transition, again for this one - that is enough to crash the renderer on an
        // iPhone. On a size tap, where the bar only swaps which chips are lit, the toolbar is the
        // same height either side and the grid was being snapshotted to travel nowhere.
        const types = ['plp-toolbar-refresh'];

        if (toolbarShape(this) !== toolbarShape(responseDocument)) types.push('plp-toolbar-resize');

        return startViewTransition(applyRefresh, types);
      })
      .catch((error) => console.warn('[plp-toolbar] refresh failed:', error))
      .finally(() => this.removeAttribute('data-updating'));
  };
}

if (!customElements.get('plp-toolbar-component')) {
  customElements.define('plp-toolbar-component', PlpToolbar);
}
